import React from "react";
import { useEffect } from "react";
import { Grid } from "@material-ui/core";
import { Controller, useWatch } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { Select } from "@ofqual/portal-components/components/Select";
import {  useLookup } from "@ofqual/portal-core/core/hooks";
import { Error } from "../common/Error";
import { fieldLabels } from "./fieldLabels";

import {
  generalConditionsService,
  generalSubConditionsService,
  qualificationConditionsService,
  subjectConditionsService,
  yesNoService,
} from "../../services";

export const Compliance: React.FC<SectionProps<EventNotification>> = ({
  control,
  errors,
  trigger,
  disabled,
  lookupTracker,
}) => {
  const [
    generalConditionsLookup,
    generalSubConditionsLookup,
    qualificationConditionsLookup,
    subjectConditionsLookup,
    yesNoLookup,
  ] = useLookup(
    generalConditionsService,
    generalSubConditionsService,
    qualificationConditionsService,
    subjectConditionsService,
    yesNoService
  );

  const {
    declaringNonCompliance,
    eventNotificationDeclaredNonComplianceGeneralCondition,
    eventNotificationDeclaredNonComplianceGeneralSubCondition,
    eventNotificationDeclaredNonComplianceQualCondition,
    eventNotificationDeclaredNonComplianceSubjectCondition,
  } = useWatch({
    control,
    name: [
      "declaringNonCompliance",
      "eventNotificationDeclaredNonComplianceGeneralCondition",
      "eventNotificationDeclaredNonComplianceGeneralSubCondition",
      "eventNotificationDeclaredNonComplianceQualCondition",
      "eventNotificationDeclaredNonComplianceSubjectCondition",
    ],
  });

  const isConditionSelected =
    (eventNotificationDeclaredNonComplianceGeneralCondition &&
      eventNotificationDeclaredNonComplianceGeneralCondition.length) ||
    (eventNotificationDeclaredNonComplianceQualCondition &&
      eventNotificationDeclaredNonComplianceQualCondition.length) ||
    (eventNotificationDeclaredNonComplianceSubjectCondition &&
      eventNotificationDeclaredNonComplianceSubjectCondition.length);

  useEffect(() => {
    // Revalidate the form when the conditions change
    trigger('eventNotificationDeclaredNonComplianceGeneralCondition');
    trigger('eventNotificationDeclaredNonComplianceGeneralSubCondition');
    trigger('eventNotificationDeclaredNonComplianceQualCondition');
    trigger('eventNotificationDeclaredNonComplianceSubjectCondition');
  }, [
    declaringNonCompliance,
    eventNotificationDeclaredNonComplianceGeneralCondition,
    eventNotificationDeclaredNonComplianceQualCondition,
    eventNotificationDeclaredNonComplianceSubjectCondition,
  ]);

  let subConditionReadOnly = function (): boolean {

    let readOnly = false;

    if (eventNotificationDeclaredNonComplianceGeneralCondition.length === 0 &&
        eventNotificationDeclaredNonComplianceGeneralSubCondition.length === 0)
     {
      readOnly = true;      
     }
    return readOnly;
  }

  let subConditionValid = function (): boolean {

    let valid = true;

    if (eventNotificationDeclaredNonComplianceGeneralSubCondition.length > 0 && eventNotificationDeclaredNonComplianceGeneralCondition.length === 0) {
      valid = false
    }

    let items = generalSubConditionsLookup.filter((el: any) => {
      return eventNotificationDeclaredNonComplianceGeneralCondition.some((f: any) => {
        return f === el.parentId;
      })
    }).map((i) => i.id)

    if (items.length > 0 && (eventNotificationDeclaredNonComplianceGeneralSubCondition &&
      eventNotificationDeclaredNonComplianceGeneralSubCondition.length)) {

      if (eventNotificationDeclaredNonComplianceGeneralSubCondition.filter((x: string | number | boolean) => !items.includes(x)).length > 0) {
        valid = false;
      }
    }
    return valid;
  }


  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="declaringNonCompliance"
          as={
            <Select
              label={fieldLabels.declaringNonCompliance}
              readOnly={disabled}
              datatest="declaring-non-compliance"
              lookupTracker={lookupTracker}
            >
              {yesNoLookup}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationDeclaredNonComplianceGeneralCondition"
          rules={{
            validate: (value) =>
              !isConditionSelected && declaringNonCompliance
                ? !!value.length
                : undefined,
          }}
          as={
            <Select
              label="General Conditions"
              readOnly={disabled || !declaringNonCompliance}
              selectVariant="multiple"
              datatest="declared-general-conditions"
              lookupTracker={lookupTracker}
            >
              {generalConditionsLookup}
            </Select>
          }
        />
        {errors.eventNotificationDeclaredNonComplianceGeneralCondition && (
          <Error>You must select one condition</Error>
        )}
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationDeclaredNonComplianceGeneralSubCondition"
          rules={{
            validate: (value) => subConditionValid() === false ? false : true
          }}
          as={
            <Select
              label={fieldLabels["eventNotificationDeclaredNonComplianceGeneralSubCondition"]}
              readOnly={disabled || !declaringNonCompliance || subConditionReadOnly()}
              selectVariant="multiple"
              datatest="declared-general-sub-conditions"
              lookupTracker={lookupTracker}
            >
              {generalSubConditionsLookup.filter((el: any) => {
                return eventNotificationDeclaredNonComplianceGeneralSubCondition.some((f: any) => {
                  return f === el.id;
                }) || eventNotificationDeclaredNonComplianceGeneralCondition.some((f: any) => {
                  return f === el.parentId;
                })
              })}
            </Select>
          }
        />
        {errors.eventNotificationDeclaredNonComplianceGeneralSubCondition && (
          <Error>General sub conditions need to match general conditions</Error>
        )}
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationDeclaredNonComplianceQualCondition"
          rules={{
            validate: (value) =>
              !isConditionSelected && declaringNonCompliance
                ? !!value.length
                : undefined,
          }}
          as={
            <Select
              label="Qualification Conditions"
              readOnly={disabled || !declaringNonCompliance}
              selectVariant="multiple"
              datatest="declared-qual-conditions"
              lookupTracker={lookupTracker}
            >
              {qualificationConditionsLookup}
            </Select>
          }
        />
        {errors.eventNotificationDeclaredNonComplianceQualCondition && (
          <Error>You must select one condition</Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationDeclaredNonComplianceSubjectCondition"
          rules={{
            validate: (value) =>
              !isConditionSelected && declaringNonCompliance
                ? !!value.length
                : undefined,
          }}
          as={
            <Select
              label="Subject Conditions"
              readOnly={disabled || !declaringNonCompliance}
              selectVariant="multiple"
              datatest="declared-subject-conditions"
              lookupTracker={lookupTracker}
            >
              {subjectConditionsLookup}
            </Select>
          }
        />
        {errors.eventNotificationDeclaredNonComplianceSubjectCondition && (
          <Error>You must select one condition</Error>
        )}
      </Grid>
    </Grid>
  );
};
